import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

// GO4SINSIGHTS chart (CDC §5.7–5.9). The server sends a ready-made chart
// (Insights::Charts): labels, series with their values AND formatted
// displays. Tooltips show the formatted value; a gap (null) stays a gap,
// never a zero. Colours are neutral design-system tokens: no favourable /
// unfavourable colour without a rule that judges the value.
export default class extends Controller {
  static targets = ["canvas"];
  static values = { chart: Object };

  connect() {
    const spec = this.chartValue;
    if (!spec || !this.hasCanvasTarget) return;

    const palette = [this.token("--g4s-info-fg", "#1D4ED8"), this.token("--ds-text-4", "#94A3B8")];
    const stacked = spec.type === "stacked_bar";
    const type = spec.type === "line" ? "line" : "bar";
    const horizontal = !!spec.horizontal;
    const valueAxis = { stacked, beginAtZero: true, ticks: { callback: (v) => this.tick(v, spec.unit) } };
    const categoryAxis = { stacked, grid: { display: false }, ticks: { maxRotation: 0, autoSkip: !horizontal } };

    this.chart = new Chart(this.canvasTarget.getContext("2d"), {
      type,
      data: {
        labels: spec.labels,
        datasets: spec.series.map((serie, index) => ({
          label: serie.label,
          data: serie.values,
          displays: serie.displays,
          backgroundColor: this.alpha(palette[index % palette.length], type === "line" ? 0.12 : 0.85),
          borderColor: palette[index % palette.length],
          borderWidth: type === "line" ? 2 : 0,
          borderRadius: type === "line" ? 0 : 4,
          fill: type === "line",
          tension: 0.25,
          spanGaps: false,
          pointRadius: 3,
          barPercentage: spec.type === "histogram" ? 1.0 : 0.7,
          categoryPercentage: spec.type === "histogram" ? 0.95 : 0.8,
        })),
      },
      options: {
        indexAxis: horizontal ? "y" : "x",
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: spec.series.length > 1, position: "bottom", labels: { boxWidth: 10 } },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label} : ${ctx.dataset.displays[ctx.dataIndex] ?? "—"}`,
            },
          },
        },
        // Value axis starts at zero (CDC §5.7: comparative bars).
        scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
      },
    });
  }

  disconnect() {
    this.chart?.destroy();
  }

  tick(value, unit) {
    if (unit === "percent") return `${value} %`;
    if (unit === "currency") return value >= 1e6 ? `${(value / 1e6).toLocaleString("fr-FR")} M` : value.toLocaleString("fr-FR");
    return value.toLocaleString("fr-FR");
  }

  token(name, fallback) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  alpha(color, opacity) {
    const hex = color.replace("#", "");
    if (hex.length !== 6) return color;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
}
